import { TEmEnumsResponsePayload } from 'redux/reducers/emManagement/types';
import { TFilters, TPageResponse } from 'redux/types';
import { ItemWithId } from 'ui/EasTable/types';

const DEFAULT_CURRENCY = 'EUR';

/** Resolves a currency id coming from the API into its code using the supported_currency enum. */
export const resolveCurrencyName = (
	supportedCurrency: TEmEnumsResponsePayload,
	currencyId: number | null | undefined,
): string =>
	supportedCurrency?.find((item) => String(item.value) === String(currencyId))?.displayName ??
	DEFAULT_CURRENCY;

/**
 * Gives the rows of a paged response the `id` the table needs as a row key. Some sections return
 * rows without one, so it is derived from the position in the whole list, which stays stable as long
 * as the page is not reloaded with different data.
 */
export const withRowIds = <TItem>(
	list: TPageResponse<TItem> | null,
): TPageResponse<TItem & ItemWithId> | null => {
	if (!list) return null;
	const offset = ((list.pageNumber || 1) - 1) * (list.pageSize || 0);
	return {
		...list,
		data: (list.data ?? []).map((item, index) => ({ ...item, id: offset + index + 1 })),
	};
};

const PERIOD_PATTERN = /^(\d{4})-(\d{2})$/;

/**
 * Joins the year and the month a section returns separately into the `YYYY-MM` a single Date column
 * shows and the year-month picker works with.
 */
export const withPeriod = <TItem extends { year: number; month: number }>(
	list: TPageResponse<TItem> | null,
): TPageResponse<TItem & { period: string }> | null =>
	list
		? {
				...list,
				data: (list.data ?? []).map((item) => ({
					...item,
					period:
						item.year && item.month ? `${item.year}-${String(item.month).padStart(2, '0')}` : '',
				})),
			}
		: null;

/**
 * The picker leaves a `YYYY-MM` value in the url, while the endpoints know the year and the month as
 * two separate fields — so one picked period becomes two filters.
 */
export const buildPeriodFilters = (period: string): TFilters => {
	const match = PERIOD_PATTERN.exec(period);
	if (!match) return [];
	return [
		{ fieldName: 'year', value: String(match[1]), operator: '=' },
		{ fieldName: 'month', value: String(match[2]), operator: '=' },
	];
};
